import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { API } from '@/api'
import { ServerPrivateRegionInfo } from '@/api/type-server'
import { message } from '@/components/base/Notification'

export function useDomainSelection() {
  const { t } = useTranslation('privateRegion')
  const [searchParams, setSearchParams] = useSearchParams()
  const [domainInfos, setDomainInfos] = useState<ServerPrivateRegionInfo[]>([])
  const [loading, setLoading] = useState(false)

  // Read selected region from URL query parameter
  const urlRegion = searchParams.get('region')
  const selectedRegion = urlRegion ? Number(urlRegion) : null

  const loadDomains = useCallback(async () => {
    setLoading(true)
    try {
      const res = await API.getPrivateRegionInfo({ region: -1 })
      if (res.code === 0) {
        const regions = res.infos
        setDomainInfos(regions)

        // Set URL param to first region if none selected
        if (regions.length > 0 && !urlRegion) {
          setSearchParams({ region: regions[0].region.toString() }, { replace: true })
        }

        // Validate URL region is valid, reset if not
        if (urlRegion && !regions.some((r) => r.region === Number(urlRegion))) {
          setSearchParams({ region: regions[0].region.toString() }, { replace: true })
        }
      }
    } catch (error) {
      message.error(t('errors.failedToLoadRegions'))
    } finally {
      setLoading(false)
    }
  }, [t]) // Only run once on mount

  const setSelectedRegion = useCallback(
    (domain: number) => {
      setSearchParams({ region: domain.toString() })
    },
    [setSearchParams],
  )

  useEffect(() => {
    loadDomains()
  }, [loadDomains])

  return {
    domainInfos,
    selectedRegion,
    setSelectedRegion,
    loading,
  }
}
